import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Phone, Calendar, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { usePatientSearch, PatientSearchResult } from "@/hooks/usePatientSearch";
import { UI_CONSTANTS } from "@/lib/constants";
import { displayAge } from "@/lib/patientAge";

interface PatientSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (patient: PatientSearchResult) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  onEnterNext?: () => void;
  allowCreateNew?: boolean;
  onCreateNew?: (searchTerm: string) => void;
  inputSize?: 'xs' | 'sm' | 'md';
  autoFocus?: boolean;
  /** Called after the input loses focus (and any dropdown click is handled). */
  onBlur?: () => void;
  /** Called on Escape when the dropdown is already closed. */
  onEscape?: () => void;
}

const SIZE_CLASSES: Record<string, string> = {
  xs: "text-xs py-0.5",
  sm: "text-sm py-1",
  md: "text-sm py-2",
};

function formatDate(dateString?: string) {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getGenderBadgeColor(gender?: string) {
  switch (gender?.toLowerCase()) {
    case 'male':
    case 'm':
      return 'bg-info/10 text-info';
    case 'female':
    case 'f':
      return 'bg-medical-vitals/10 text-medical-vitals';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function genderLabel(gender?: string) {
  if (!gender) return null;
  if (gender === 'M' || gender === 'Male') return 'Male';
  if (gender === 'F' || gender === 'Female') return 'Female';
  return gender;
}

const PatientSearchInput = forwardRef<HTMLInputElement, PatientSearchInputProps>(
  ({ value, onChange, onSelect, placeholder = "Search patient by name, phone, or MRN...", className, containerClassName, onEnterNext, allowCreateNew, onCreateNew, inputSize = 'sm', autoFocus, onBlur, onEscape }, ref) => {
    const [inputValue, setInputValue] = useState(value ?? "");
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);

    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

    const { patients, isLoading } = usePatientSearch(inputValue);

    const minLen = UI_CONSTANTS.MIN_PATIENT_SEARCH_LENGTH;
    const showCreateNew = !!allowCreateNew && !isLoading && patients.length === 0 && inputValue.trim().length >= minLen;
    const itemCount = patients.length + (showCreateNew ? 1 : 0);

    useEffect(() => { setInputValue(value ?? ""); }, [value]);
    useEffect(() => { setHighlightedIndex(0); }, [patients, showCreateNew]);

    useEffect(() => {
      if (isOpen && optionRefs.current[highlightedIndex] && dropdownRef.current) {
        const el = optionRefs.current[highlightedIndex];
        const dropdown = dropdownRef.current;
        if (el) {
          if (el.offsetTop < dropdown.scrollTop)
            dropdown.scrollTop = el.offsetTop;
          else if (el.offsetTop + el.offsetHeight > dropdown.scrollTop + dropdown.clientHeight)
            dropdown.scrollTop = el.offsetTop + el.offsetHeight - dropdown.clientHeight;
        }
      }
    }, [highlightedIndex, isOpen]);

    useEffect(() => {
      optionRefs.current = optionRefs.current.slice(0, itemCount);
      if (highlightedIndex >= itemCount && itemCount > 0)
        setHighlightedIndex(itemCount - 1);
    }, [itemCount, highlightedIndex]);

    const handleSelect = (patient: PatientSearchResult, e: React.MouseEvent) => {
      e.preventDefault();
      setInputValue(patient.name);
      onSelect(patient);
      setIsOpen(false);
      if (onEnterNext) setTimeout(onEnterNext, 100);
    };

    const handleCreateNew = (e: React.MouseEvent) => {
      e.preventDefault();
      onCreateNew?.(inputValue.trim());
      setIsOpen(false);
    };

    const showDropdown = isOpen && inputValue.trim().length >= minLen;

    const getDropdownStyle = (): React.CSSProperties => {
      if (!containerRef.current) return {};
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 320);
      const dropdownHeight = Math.min(itemCount * 64 + 16, 320);
      const wouldOverflowBottom = rect.bottom + dropdownHeight > window.innerHeight - 20;
      const wouldOverflowRight = rect.left + width > window.innerWidth - 20;
      return {
        position: "fixed",
        width: `${width}px`,
        ...(wouldOverflowBottom
          ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
          : { top: rect.bottom + 4 }),
        ...(wouldOverflowRight
          ? { right: window.innerWidth - rect.right, left: "auto" }
          : { left: rect.left }),
      };
    };

    return (
      <div ref={containerRef} className={cn("relative", containerClassName)}>
        <input
          ref={ref}
          type="text"
          value={inputValue}
          autoComplete="off"
          autoFocus={autoFocus}
          placeholder={placeholder}
          className={cn(
            "w-full px-2 rounded-md border border-input bg-background outline-none transition-colors",
            "focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/60",
            SIZE_CLASSES[inputSize],
            className
          )}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            onChange(val);
            setIsOpen(val.trim().length >= minLen);
          }}
          onFocus={() => {
            if (inputValue.trim().length >= minLen) setIsOpen(true);
          }}
          onClick={() => {
            if (inputValue.trim().length >= minLen) setIsOpen(true);
          }}
          onBlur={() => {
            setTimeout(() => setIsOpen(false), 200);
            if (onBlur) setTimeout(onBlur, 200);
          }}
          onKeyDown={(e) => {
            if (!isOpen) {
              if (e.key === "Enter" && onEnterNext) { e.preventDefault(); onEnterNext(); }
              if (e.key === "Escape" && onEscape) { e.preventDefault(); onEscape(); }
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlightedIndex((p) => Math.min(p + 1, itemCount - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlightedIndex((p) => Math.max(p - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              if (highlightedIndex >= 0 && highlightedIndex < patients.length) {
                const patient = patients[highlightedIndex];
                setInputValue(patient.name);
                onSelect(patient);
                setIsOpen(false);
                if (onEnterNext) setTimeout(onEnterNext, 100);
              } else if (showCreateNew && highlightedIndex === patients.length) {
                onCreateNew?.(inputValue.trim());
                setIsOpen(false);
              } else if (onEnterNext) {
                onEnterNext();
              }
            } else if (e.key === "Escape") {
              setIsOpen(false);
            } else if (e.key === "Tab") {
              setIsOpen(false);
            }
          }}
        />

        {showDropdown && createPortal(
          <div
            ref={dropdownRef}
            data-patient-search-dropdown=""
            className="z-[9999] bg-popover shadow-lg border border-border rounded-md max-h-80 overflow-auto text-sm ring-1 ring-border"
            style={{ ...getDropdownStyle(), pointerEvents: "auto" }}
          >
            {isLoading && (
              <div className="px-3 py-4 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}

            {!isLoading && patients.map((patient, i) => (
              <div
                key={patient.id}
                ref={(el) => { optionRefs.current[i] = el; }}
                className={cn(
                  "px-3 py-2 cursor-pointer transition-colors border-b border-border/40 last:border-0",
                  i === highlightedIndex
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent text-foreground"
                )}
                onMouseEnter={() => setHighlightedIndex(i)}
                onMouseDown={(e) => handleSelect(patient, e)}
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{patient.name}</span>
                  {patient.gender && (
                    <Badge className={cn("text-[10px] px-1.5 py-0 shrink-0", i === highlightedIndex ? "bg-primary-foreground/20 text-primary-foreground" : getGenderBadgeColor(patient.gender))}>
                      {genderLabel(patient.gender)}
                    </Badge>
                  )}
                  {(() => {
                    const ageStr = displayAge(patient.age, patient.date_of_birth);
                    return ageStr ? (
                      <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 shrink-0", i === highlightedIndex && "border-primary-foreground/40 text-primary-foreground")}>
                        {ageStr}
                      </Badge>
                    ) : null;
                  })()}
                </div>
                <div className={cn(
                  "flex items-center gap-3 mt-0.5 text-xs",
                  i === highlightedIndex ? "text-primary-foreground/75" : "text-muted-foreground"
                )}>
                  {patient.phone && (
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{patient.phone}</span>
                  )}
                  {patient.last_visit && (
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(patient.last_visit)}</span>
                  )}
                  {patient.mrn && (
                    <span>MRN: {patient.mrn}</span>
                  )}
                </div>
              </div>
            ))}

            {!isLoading && patients.length === 0 && !showCreateNew && (
              <div className="px-3 py-3 text-muted-foreground text-center text-xs italic">
                No patients found
              </div>
            )}

            {showCreateNew && (
              <div
                ref={(el) => { optionRefs.current[patients.length] = el; }}
                className={cn(
                  "px-3 py-2 cursor-pointer transition-colors flex items-center gap-2 font-medium",
                  highlightedIndex === patients.length
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent text-foreground"
                )}
                onMouseEnter={() => setHighlightedIndex(patients.length)}
                onMouseDown={handleCreateNew}
              >
                <Plus className="w-3.5 h-3.5" />
                Create new patient "{inputValue.trim()}"
              </div>
            )}
          </div>,
          document.body
        )}
      </div>
    );
  }
);

PatientSearchInput.displayName = "PatientSearchInput";
export default PatientSearchInput;
